// Drift: kontorets bookinger, kunder, log, overblik og dashboard på Supabase (del af SupabaseApi – se supabase.ts).
// Læsning: færdige visninger i ét kald (20261027090000_kontor_laes.sql) – hurtigt, selv med titusindvis af bookinger.
// Skrivning: databasefunktioner (20261027120000_kontor_skriv.sql), der tjekker rettigheden, regner prisen, låser og logger.
// Felterne tjekkes først her med de samme regler som testversionen, så fejlen står ved det rigtige felt.
import { ApiFejl, type Api, type Betaling, type BookingSide, type BookingVisning, type Historik, type KundeRaekke, type KundeSide, type LogVisning, type ManglerTidRaekke } from './api';
import type { Bil, Booking, KundeNote, Kunde, Steamer, Ydelse } from '../../core/typer';
import { fraDb } from '../../core/raekker';
import { iDag, lokalMin } from '../../core/dato';
import { regler } from '../../core/motor';
import { ruteDag, ruteResultat, tidsFlyt, tjekRuteFlyt, type RuteBesked } from '../../core/ruteoptimering';
import { gyldigtPostnr } from '../../core/geo';
import { email as tjekEmail, gyldigDato, nummerplade as tjekPlade, rens, telefon as tjekTelefon } from '../../core/validering';
import { beregnDashboard } from '../../core/dashboard';
import { skalRinges, tjekFrigiv } from '../../core/manglerTid';
import { normFastFri } from '../../core/fastFri';
import { flytForslag, type BeroertBooking } from '../../core/fastFriFlyt';
import type { DagsOpgave } from '../../core/ledige';
import type { SbKatalog, SbKontekst } from './sbKatalog';
import type { SbMedlemmer } from './sbMedlemmer';

type R = Record<string, unknown>;
type Raa = { b: R; kunde: Kunde | null; bil: Bil | null; ydelse: R | null; steamer: R | null; tilvalgNavne: BookingVisning['tilvalgNavne'] };

/** Databasens visning → BookingVisning (samme form som i testversionen). */
export function tilVisning(x: Raa): BookingVisning {
  const b = fraDb.booking(x.b);
  const kunde = x.kunde ?? { id: b.kunde_id, navn: 'Ukendt kunde', email: '', telefon: '', adresse: '', postnr: '', by: '', oprettet: '' };
  return {
    ...b, dato: b.dato ?? '',
    kunde: { ...kunde, oprettet: String(kunde.oprettet ?? '').slice(0, 10) },
    bil: x.bil ?? { id: b.bil_id ?? '', kunde_id: b.kunde_id, maerke_model: '', nummerplade: '', biltype_id: '' },
    ydelse: x.ydelse ? fraDb.ydelse(x.ydelse) : ({ id: b.ydelse_id, navn: b.ydelse_id, beskrivelse: '', farve: '#0F766E', kategori_id: null, aktiv: false, online: false, sortering: 99 } as Ydelse),
    steamer: x.steamer ? (fraDb.steamer(x.steamer) as Steamer) : null,
    tilvalgNavne: x.tilvalgNavne ?? [],
  };
}
const liste = (l: unknown) => ((l as Raa[] | null) ?? []).map(tilVisning);
const tilKundeRaekke = (k: R): KundeRaekke => ({
  ...(k as unknown as Kunde), oprettet: String(k.oprettet ?? ''), biler: (k.biler as Bil[]) ?? [], antal: Number(k.antal ?? 0), medlem: !!k.medlem, naeste: (k.naeste as string | null) ?? null,
  medlem_interval: k.medlem_interval == null ? null : Number(k.medlem_interval), seneste: (k.seneste as KundeRaekke['seneste']) ?? null,
  omsaetning: Number(k.omsaetning ?? 0), ubetalt: Number(k.ubetalt ?? 0), brands: (k.brands as string[]) ?? [],
});
const tjek = (f: Record<string, string>) => { if (Object.keys(f).length) throw new ApiFejl('Tjek de markerede felter.', f); };
type DashRaa = { bookinger: unknown[][]; medlemskaber: unknown[][]; skadesager?: [string | null, string][] };
type LikvDbRaa = { dage: [string, string, number, number, number][]; udestaaende: number; hos_rykker: number; forventet: number; forventet_medlem: number };

export class SbKontor {
  constructor(private readonly c: SbKontekst, private readonly kat: SbKatalog, private readonly medlem: SbMedlemmer) {}
  private async rpc<T>(navn: string, args: R): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }

  // ---------- Bookinger ----------
  bookinger: Api['bookinger'] = async (f) => liste(await this.rpc('bookinger_liste', {
    fra: f.fra ?? null, til: f.til ?? null, p_status: f.status ?? 'alle', soeg: rens(f.soeg ?? '', 80), p_steamer: f.steamer_id ?? null, nye_dage: f.nyeDage ?? 0,
  }));
  bookingSide: Api['bookingSide'] = async (f) => {
    const x = await this.rpc<{ raekker: unknown; total: number; alle: number; prStatus: BookingSide['prStatus']; valg?: BookingSide['valg'] }>('bookinger_side', { p: { ...f, soeg: rens(f.soeg ?? '', 80) } });
    return { ...x, raekker: liste(x.raekker) };
  };
  booking: Api['booking'] = async (id) => {
    const [x, h] = await Promise.all([this.rpc<Raa | null>('booking_visning', { p_id: id }), this.rpc<Betaling[] | null>('booking_historiske_betalinger', { p_id: id }).catch(() => null)]);
    return x ? { ...tilVisning(x), historiske: h ?? [] } : null;
  };
  historik: Api['historik'] = async (id) => ((await this.rpc<Historik[] | null>('booking_historik', { p_id: id })) ?? []);
  /** Steamere (med arbejdstider og fravær) og lukkedage ændres sjældent: huskes kort, så bladring i kalenderen kun henter bookingerne.
   *  Glemmes ved enhver ændring i appen (glemStam fra skift). */
  private stam: { t: number; p: Promise<[Awaited<ReturnType<SbKatalog['steamere']>>, Awaited<ReturnType<SbKatalog['lukkedage']>>]> } | null = null;
  glemStam() { this.stam = null; }
  private hentStam() {
    if (!this.stam || Date.now() - this.stam.t > 60_000) {
      const p = Promise.all([this.kat.steamere(), this.kat.lukkedage()]);
      this.stam = { t: Date.now(), p };
      p.catch(() => { if (this.stam?.p === p) this.stam = null; });
    }
    return this.stam.p;
  }
  dag: Api['dag'] = async (dato) => {
    const [bs, [st, luk]] = await Promise.all([this.rpc('bookinger_periode', { fra: dato, til: dato }), this.hentStam()]);
    return {
      bookinger: liste(bs), steamere: st.steamere.filter((s) => s.aktiv), arbejdstider: st.arbejdstider,
      fravaer: st.fravaer.filter((f) => f.fra <= dato && f.til >= dato), saerligeDage: st.saerligeDage.filter((s) => s.dato === dato),
      lukkedag: luk.find((l) => l.dato === dato) ?? null,
    };
  };
  periode: Api['periode'] = async (fra, til) => {
    const [bs, [st, luk]] = await Promise.all([this.rpc('bookinger_periode', { fra, til }), this.hentStam()]);
    return { bookinger: liste(bs), steamere: st.steamere.filter((s) => s.aktiv), fravaer: st.fravaer.filter((f) => f.til >= fra && f.fra <= til), lukkedage: luk.filter((l) => l.dato >= fra && l.dato <= til) };
  };
  /** Afventende bookinger. Forslaget er bookingens egen tid og steamer (beregnede alternativer kommer med ruteplanen). */
  afventende: Api['afventende'] = async () => liste(await this.rpc('bookinger_afventende', {})).map((v) => ({
    ...v, forslag: v.steamer_id && v.start != null && v.slut != null && v.dato >= iDag()
      ? { steamer_id: v.steamer_id, navn: v.steamer?.navn ?? '', start: v.start, slut: v.slut, ekstraKoersel: 0, koerselFoer: 0 } : null,
  }));

  godkend: Api['godkend'] = async (id, valg) => {
    await this.rpc('godkend_booking', { p_id: id, p_steamer: valg?.steamer_id ?? null, p_start: valg?.start ?? null, p_dato: valg?.dato ?? null });
    this.c.skift();
  };
  afvis: Api['afvis'] = async (id, aarsag) => {
    if (rens(aarsag, 500).length < 3) throw new ApiFejl('Skriv en kort begrundelse til kunden.', { aarsag: 'Skriv en begrundelse.' });
    await this.rpc('afvis_booking', { p_id: id, p_aarsag: aarsag });
    this.c.skift();
  };
  /** Ledige tider og steamere til en booking (regnes på serveren med samme motor som bookingsiden). */
  forslag: Api['forslag'] = (a) => this.kat.kald('forslag', { booking_id: a.booking_id ?? null, dato: a.dato, postnr: a.postnr, varighed: a.varighed, kunSteamer: a.kunSteamer ?? null, ydelse_id: a.ydelse_id ?? null });
  retBooking: Api['retBooking'] = async (id, r) => {
    if (r.dato !== undefined && !gyldigDato(r.dato)) throw new ApiFejl('Ugyldig dato.');
    if (r.telefon2 && !tjekTelefon(r.telefon2)) throw new ApiFejl('Telefon 2 skal have 8 cifre.', { telefon2: 'Skriv 8 cifre.' });
    if (r.postnr !== undefined && !gyldigtPostnr(r.postnr)) throw new ApiFejl('Skriv et postnummer med 4 cifre.', { postnr: 'Skriv 4 cifre.' });
    const svar = await this.rpc<{ kundeBesked: boolean; kommende?: { flyttet: number; sprunget: number } }>('ret_booking', { p_id: id, r });
    this.c.skift();
    return svar;
  };
  saetBookingBetaling: Api['saetBookingBetaling'] = async (id, p) => {
    await this.rpc('saet_booking_betaling', { p_id: id, p });
    this.c.skift();
  };
  // ---------- Mangler tid (20261028850000_mangler_tid.sql) ----------
  frigivTid: Api['frigivTid'] = async (id, v) => {
    const r = await this.rpc<{ token: string }>('frigiv_tid', { p_id: id, p: tjekFrigiv(v) });
    this.c.skift();
    return r;
  };
  manglerTid: Api['manglerTid'] = async () => {
    const x = await this.rpc<{ raekker: (Raa & { mt: ManglerTidRaekke['mt'] })[]; personer: { id: string; navn: string }[]; spaerret: boolean }>('mangler_tid_liste', {});
    const nu = Date.now();
    const raekker = (x.raekker ?? []).map((r) => ({ ...tilVisning(r), mt: r.mt }))
      .sort((a, c) => Number(skalRinges(c.mt, nu)) - Number(skalRinges(a.mt, nu)) || a.mt.oprettet.localeCompare(c.mt.oprettet));
    return { raekker, personer: x.personer ?? [], spaerret: !!x.spaerret };
  };
  kontorBooking: Api['kontorBooking'] = async (i) => {
    const f: Record<string, string> = {};
    if (rens(i.navn, 100).length < 2) f.navn = 'Skriv kundens navn.';
    if (!tjekTelefon(i.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    if (i.email && !tjekEmail(i.email)) f.email = 'Ugyldig e-mail.';
    if (rens(i.adresse, 150).length < 3) f.adresse = 'Skriv adressen.';
    if (!gyldigtPostnr(i.postnr)) f.postnr = 'Skriv et postnummer.';
    if (rens(i.by, 60).length < 2) f.by = 'Skriv byen.';
    if (!gyldigDato(i.dato)) f.dato = 'Vælg en dato.';
    i.biler.forEach((b, n) => { if (!(i.uden_plade && !b.nummerplade.trim()) && !tjekPlade(b.nummerplade)) f[`bil${n}_nummerplade`] = 'Skriv nummerpladen.'; });
    if (i.telefon2 && !tjekTelefon(i.telefon2)) f.telefon2 = 'Skriv 8 cifre.';
    tjek(f);
    const r = await this.rpc<{ id: number; antal: number }>('opret_kontor_booking', { p: { ...i, felter: { kunde: i.felter ?? {}, booking: i.felter ?? {} } } });
    this.c.skift();
    return r;
  };
  saetPris: Api['saetPris'] = async (booking_id, total, grund) => {
    if (rens(grund, 300).length < 3) throw new ApiFejl('Skriv hvorfor prisen ændres (vises i historikken).');
    await this.c.k(this.c.sb.rpc('saet_pris', { p_booking: booking_id, p_total: Math.round(total), p_grund: grund }));
    this.c.skift();
  };

  // ---------- Ruteoptimering (ejer/admin) ----------
  // Forslagene regnes her med samme motor som testversionen (src/core/ruteoptimering.ts) på dagens plan fra databasen;
  // databasen (anvend_ruteforslag) tjekker de faste regler igen: rettighed, samme dag, uændret plan, overlap og max flytning.
  private async ruteData(dato: string) {
    if (!gyldigDato(dato)) throw new ApiFejl('Ugyldig dato.');
    const [besked, dag, zoner, ind] = await Promise.all([this.rpc<RuteBesked[] | null>('rute_besked_liste', { p_dato: dato }), this.dag(dato), this.kat.zoner(), this.kat.indstillinger()]);
    const r = regler({ indst: ind });
    const tid = tidsFlyt(ind.booking.ruteFlyt, r);
    const data = ruteDag({ dato, idag: iDag(), nu: lokalMin(), flytTider: !!tid, bookinger: dag.bookinger, steamere: dag.steamere, arbejdstider: dag.arbejdstider,
      fravaer: dag.fravaer, saerligeDage: dag.saerligeDage, lukkedage: dag.lukkedag ? [dato] : [], zoner });
    return { ...data, r, tid, dag, besked: besked ?? [] };
  }
  ruteForslag: Api['ruteForslag'] = async (dato) => {
    const { jobs, steamere, r, tid, dag, besked } = await this.ruteData(dato);
    const navn = (id: string) => dag.steamere.find((s) => s.id === id)?.navn ?? id;
    const kunde = (id: number) => { const b = dag.bookinger.find((x) => x.id === id); return { kunde: b?.kunde.navn ?? '', by: b?.by ?? '' }; };
    return { ...ruteResultat(jobs, steamere, r, tid, navn, kunde), flytTider: tid ? { maxTimer: tid.maxMin / 60 } : null, besked };
  };
  anvendRuteForslag: Api['anvendRuteForslag'] = async (dato, flyt) => {
    if (!flyt.length) return;
    const { jobs, steamere, r, tid } = await this.ruteData(dato);
    const f = tjekRuteFlyt(jobs, steamere, r, flyt, tid);
    if (f) throw new ApiFejl(f);
    await this.rpc('anvend_ruteforslag', { p_dato: dato, p_flyt: flyt.map((m) => ({ id: m.id, fra: m.fra, til: m.til, ...(m.start != null ? { start: m.start } : {}) })) });
    this.c.skift();
  };
  // ---------- Fast fri med bookinger i tiden (20261029892000_fastfri_flyt.sql): forslagene regnes med Smartplans motor ----------
  fastFriBeroerte: Api['fastFriBeroerte'] = async (steamer_id, regel) => {
    const [r] = normFastFri([regel]);
    const x = await this.rpc<{ bookinger: BeroertBooking[]; opgaver: (DagsOpgave & { dato: string })[] }>('fastfri_beroerte', { p_steamer: steamer_id, p_ugedag: r.ugedag, p_fra: r.fra, p_til: r.til });
    const beroerte = (x?.bookinger ?? []).map((b) => ({ ...b, id: Number(b.id) }));
    if (!beroerte.length) return [];
    const [st, zoner, ind, luk] = await Promise.all([this.kat.steamere(), this.kat.zoner(), this.kat.indstillinger(), this.kat.lukkedage()]);
    return flytForslag(beroerte, {
      ...st, lukkedage: luk.map((l) => l.dato), zoner, regler: regler({ indst: ind }), opgaver: (x.opgaver ?? []).map((o) => ({ ...o, id: Number(o.id) })),
    });
  };
  ruteBeskedGivet: Api['ruteBeskedGivet'] = async (id) => { await this.rpc('rute_besked_givet', { p_id: id }); this.c.skift(); };

  // ---------- Kunder ----------
  kunder: Api['kunder'] = async (soeg) => {
    const l = (await this.rpc<R[] | null>('kunder_soeg', { soeg: rens(soeg, 80) })) ?? [];
    return l.map(tilKundeRaekke);
  };
  kunderSide: Api['kunderSide'] = async (f) => {
    const x = await this.rpc<{ raekker: R[]; total: number; taelling: KundeSide['taelling']; byer: string[] }>('kunder_side', { p: { ...f, soeg: rens(f.soeg ?? '', 80) } });
    return { ...x, raekker: (x.raekker ?? []).map(tilKundeRaekke) };
  };
  kunde: Api['kunde'] = async (id) => {
    const x = await this.rpc<R | null>('kunde_detalje', { p_id: id });
    if (!x) return null;
    const [medlemskaber, historiske] = await Promise.all([this.medlem.forKunde(id), this.rpc<Betaling[] | null>('kunde_historiske_betalinger', { p_id: id }).catch(() => null)]);
    return { kunde: x.kunde as Kunde, biler: (x.biler as Bil[]) ?? [], bookinger: liste(x.bookinger), medlemskaber, betalinger: historiske ?? [], skader: (x.skader as never[]) ?? [] };
  };
  gemKunde: Api['gemKunde'] = async (k) => {
    const f: Record<string, string> = {};
    if (rens(k.navn, 100).length < 2) f.navn = 'Skriv navnet.';
    if (k.email && !tjekEmail(k.email)) f.email = 'Ugyldig e-mail.';
    if (!tjekTelefon(k.telefon)) f.telefon = 'Skriv et mobilnummer med 8 cifre.';
    if (k.postnr && !gyldigtPostnr(k.postnr)) f.postnr = 'Ugyldigt postnummer.';
    tjek(f);
    const id = await this.rpc<string>('gem_kunde', { p: k });
    this.c.skift();
    return { id };
  };
  tilfoejBil: Api['tilfoejBil'] = async (kunde_id, b) => {
    const f: Record<string, string> = {};
    const p = tjekPlade(b.nummerplade); if (!p) f.nummerplade = 'Skriv nummerpladen, fx AB12345.';
    if (rens(b.maerke_model, 80).length < 2) f.maerke_model = 'Skriv mærke og model.';
    if (!b.biltype_id) f.biltype_id = 'Vælg biltype.';
    tjek(f);
    const id = await this.rpc<string>('tilfoej_bil', { p_kunde: kunde_id, p: { nummerplade: p, maerke_model: rens(b.maerke_model, 80), biltype_id: b.biltype_id } });
    this.c.skift();
    return { id };
  };
  // 20261029897000_gdpr_slet.sql
  kundeSletTjek: Api['kundeSletTjek'] = async (id) => ((await this.rpc<string[] | null>('kunde_slet_tjek', { p_kunde: id })) ?? []);
  sletKundePersondata: Api['sletKundePersondata'] = async (id, bekraeft) => { await this.rpc('slet_kunde_persondata', { p_kunde: id, p_bekraeft: rens(bekraeft, 120) }); this.c.skift(); };
  genaktiverKunde: Api['genaktiverKunde'] = async (id) => { await this.rpc('genaktiver_kunde', { p_kunde: id }); this.c.skift(); };   // 20261029880000_deaktiver.sql
  kundeNoter: Api['kundeNoter'] = async (kunde_id) => ((await this.rpc<KundeNote[] | null>('kunde_noter_liste', { p_kunde: kunde_id })) ?? []);
  tilfoejKundeNote: Api['tilfoejKundeNote'] = async (kunde_id, tekst, booking_id = null) => {
    if (rens(tekst, 2000).length < 2) throw new ApiFejl('Skriv noten.');
    await this.rpc('tilfoej_kunde_note', { p_kunde: kunde_id, p_tekst: tekst, p_booking: booking_id });
    this.c.skift();
  };

  // ---------- Kundecenter: tildeling (20261028400000_kunder_d.sql) ----------
  retSkade: Api['retSkade'] = async (id, r) => {
    const p: R = {};
    for (const n of ['status', 'noter', 'steamer_id', 'ansvarlig_id'] as const) if (r[n] !== undefined) p[n] = r[n];
    await this.rpc('ret_skadesag', { p_id: id, p });
    this.c.skift();
  };
  kundecenterAnsvarlige: Api['kundecenterAnsvarlige'] = async () => ((await this.rpc<{ id: string; navn: string }[] | null>('kundecenter_ansvarlige', {})) ?? []);

  // ---------- Log, overblik og dashboard ----------
  aktivitetslog: Api['aktivitetslog'] = async (f) => ((await this.rpc<LogVisning[] | null>('aktivitetslog_liste', {
    soeg: rens(f.soeg ?? '', 80), p_type: f.type ?? null, p_hvem: f.hvem ?? null, p_fra: f.fra || null, p_til: f.til || null,
  })) ?? []);
  overblik: Api['overblik'] = (dato) => this.rpc('overblik', { p_dato: dato });
  /** Rå tal fra databasen, regnet ud med samme kode som testversionen (src/core/dashboard.ts). */
  dashboard: Api['dashboard'] = async (fra, til, steamer_id = null, brand_ids = null, visning = 'likviditet') => {
    if (!gyldigDato(fra) || !gyldigDato(til) || fra > til) throw new ApiFejl('Vælg en gyldig periode.');
    const k = await this.c.kraevKlient();
    const navne = async (t: 'ydelser' | 'tilvalg') => ((await this.c.k(this.c.sb.from(t).select('id, navn').eq('klient_id', k))) as { id: string; navn: string }[]);
    const [d, l, ydelser, tilvalg, st, luk] = await Promise.all([this.rpc<DashRaa>('dashboard_data', { p_fra: fra, p_til: til }),
      this.rpc<LikvDbRaa>('dashboard_likviditet', { p_fra: fra, p_til: til, p_steamer: steamer_id || null, p_brands: brand_ids?.length ? brand_ids : null }),
      navne('ydelser'), navne('tilvalg'), this.kat.steamere(), this.kat.lukkedage()]);
    if (steamer_id && !st.steamere.some((s) => s.id === steamer_id)) throw new ApiFejl('Steameren findes ikke.');
    const bookinger = d.bookinger.map(([id, dato, status, total, pris, steamer, ydelse, medlemskab, tilvalg, ekstra, gebyrer, betalt, start, slut, brand]) => ({
      id, dato, status, total, pris, steamer_id: steamer, ydelse_id: ydelse, medlemskab_id: medlemskab, tilvalg: tilvalg ?? [], ekstra: ekstra ?? [], gebyrer: gebyrer ?? [], betalt,
      start: start ?? null, slut: slut ?? null, brand_id: brand ?? null,
    })) as unknown as Booking[];
    const medlemskaber = d.medlemskaber.map(([id, status, start, oprettet, opsagt, pris, besoeg]) => ({
      id, status, start, oprettet: String(oprettet ?? '').slice(0, 10), opsagt: opsagt ? String(opsagt).slice(0, 10) : null, pris_pr_besoeg_inkl: pris, besoeg_pr_aar: besoeg,
    })) as never[];
    const likv = { dage: l.dage.map(([dato, metode, antal, beloeb, moms]) => ({ dato: String(dato).slice(0, 10), metode, antal: Number(antal), beloeb: Number(beloeb), moms: Number(moms) })),
      udestaaende: Number(l.udestaaende), hosRykker: Number(l.hos_rykker), forventet: Number(l.forventet), forventetMedlem: Number(l.forventet_medlem) };
    return beregnDashboard({ bookinger, likv, visning, medlemskaber, ydelser, tilvalg, steamere: st.steamere.map((s) => ({ id: s.id, navn: s.navn })), fra, til, steamer_id, brand_ids,
      skadesager: (d.skadesager ?? []).map(([steamer_id, oprettet]) => ({ steamer_id, oprettet: String(oprettet) })),
      kapacitet: { steamere: st.steamere.filter((s) => s.aktiv), arbejdstider: st.arbejdstider, fravaer: st.fravaer, saerligeDage: st.saerligeDage, lukkedage: luk.map((l) => l.dato) } });
  };
}
